// ─────────────────────────────────────────────
//  dayKey — WHICH DAY the board is, in one definition.
//
//  ⚠️ THERE WERE TWO, AND THEY DISAGREED. The client built its day key from the
//  DEVICE's local date (`rosterDateKey`), while every Cloud Function builds it
//  in `America/New_York` (functions/taskPush.js, dailyImport.js, voiceIntent.js).
//  Day-scoped documents are addressed by that key — `daily_boards/{facility}_{key}`,
//  assignments, tasks — so the two sides can address DIFFERENT DOCUMENTS for the
//  same moment. A phone in another timezone writes a confirmation the server
//  never looks at, or reads a board that does not exist: no error, just missing
//  data.
//
//  It has been latent only because every device and every user is in Florida.
//  That is a property of the current customer, not of the system.
//
//  THE DAY BELONGS TO THE HOSPITAL, NOT TO THE DEVICE. An OR board shows the
//  facility's operating day; a clinician looking at it from another state is
//  asking about that facility's day, not their own. So the zone is the FACILITY's
//  and the default matches what the functions already do — which makes the two
//  sides agree by construction instead of by luck.
//
//  Multi-tenant path: a facility document may carry `timeZone` (an IANA name).
//  Pass it and this follows it; absent, the constant below applies. Nothing
//  reads the device zone any more.
// ─────────────────────────────────────────────

// The zone the Cloud Functions already hardcode. Kept as ONE exported constant
// so a future tenant migration has a single thing to find.
export const DEFAULT_FACILITY_TZ = "America/New_York";

export const facilityTz = (facility) =>
  (facility && typeof facility.timeZone === "string" && facility.timeZone.trim()) ||
  DEFAULT_FACILITY_TZ;

// "YYYY-MM-DD" for `date` in the facility's zone.
//
// `en-CA` formats as YYYY-MM-DD, which is the key format already stored — the
// same trick domain/sheetFreshness.js uses, so the freshness gate and the board
// cannot disagree about what day it is.
export function dayKey(date = new Date(), tz = DEFAULT_FACILITY_TZ) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  try {
    return d.toLocaleDateString("en-CA", { timeZone: tz });
  } catch {
    // An invalid zone must not take the board down: fall back to the default,
    // and only then to the device, so a typo in a facility document degrades
    // to "the hospital's day" rather than to nothing.
    try {
      return d.toLocaleDateString("en-CA", { timeZone: DEFAULT_FACILITY_TZ });
    } catch {
      return d.toLocaleDateString("en-CA");
    }
  }
}

// Milliseconds until the next midnight IN THAT ZONE.
//
// ⚠️ THE ROLLOVER MUST FOLLOW THE SAME CLOCK AS THE KEY. The wall board is left
// running overnight; if the key is the facility's day but the timer fires at the
// DEVICE's midnight, a board in another zone flips hours early or late and spends
// that gap listening to the wrong day. Computed by walking forward to the first
// moment whose key differs, so it needs no offset arithmetic and handles DST —
// including the 23-hour spring-forward day — without a special case.
export function msUntilNextDay(now = new Date(), tz = DEFAULT_FACILITY_TZ) {
  const today = dayKey(now, tz);
  // Start from an hour past "now" and step in hours until the key changes, then
  // refine to the minute. Bounded at 26 hours (DST + slack).
  let coarse = null;
  for (let h = 1; h <= 26; h += 1) {
    const t = new Date(now.getTime() + h * 3600000);
    if (dayKey(t, tz) !== today) { coarse = t; break; }
  }
  if (!coarse) return 24 * 3600000;
  let fine = coarse;
  for (let m = 1; m <= 60; m += 1) {
    const t = new Date(coarse.getTime() - m * 60000);
    if (dayKey(t, tz) === today) { fine = new Date(t.getTime() + 60000); break; }
  }
  // A 5s cushion past the boundary: clock granularity must not fire a hair early
  // and re-read the same day.
  return Math.max(1000, fine.getTime() - now.getTime() + 5000);
}
